import { NoValue } from 'components/no_value/NoValue';
import { AxesEnabledDisplay } from 'components/node_settings/AxesEnabledDisplay';
import { SettingEnabledLozenge } from 'components/node_settings/SettingEnabledLozenge';
import { BorderlessSettingsSection } from 'components/node_settings/styled';
import { V4_SETTINGS_TEXT } from 'components/node_settings/v4SettingsTexts';
import { Setting } from 'components/node_settings/V4Settings/Setting';
import { SettingsList } from 'components/node_settings/V4Settings/styled';
import { useFeatureFlag } from 'contexts/configcat/useFeatureFlag';
import {
    UpdateInterval,
    V4SensorSettings,
} from 'custom_hooks/data_hooks/v4_sensor_settings/shared';
import { anyVibrationAxisEnabled } from 'pages/Settings/V4SensorSettings/helpers';
import {
    SectionDescription,
    SectionHeading,
    SectionTitle,
} from 'pages/Settings/V4SensorSettings/styled';
import { ReactNode } from 'react';
import { Lozenge } from 'shared/lozenge/Lozenge';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { getEnabledAxes, getOptionText, OptionChild } from 'utils/node';

const ValueRow = styled.div`
    display: flex;
    gap: 0.4rem;
    align-items: center;
`;

const Placeholder = styled.span`
    color: ${tokens.text.secondary};
`;

function ModeLozenge({ children }: { children: ReactNode }) {
    return (
        <Lozenge
            backgroundColor={tokens.lozenge.grey4Background}
            color={tokens.text.onColor}
        >
            {children}
        </Lozenge>
    );
}

function UpdateIntervalValue({
    updateInterval,
    updateIntervalOptions,
}: {
    updateInterval: UpdateInterval;
    updateIntervalOptions: OptionChild<number>[];
}) {
    switch (updateInterval.mode) {
        case 'smartsample':
            return <span>SMARTsample</span>;
        case 'triggered':
            return (
                <ValueRow>
                    <span>{updateInterval.thresholdG}g</span>
                    <ModeLozenge>TRIGGERED</ModeLozenge>
                </ValueRow>
            );
        case 'timed':
            return (
                <ValueRow>
                    <span>
                        {getOptionText(
                            updateIntervalOptions,
                            updateInterval.intervalMs,
                        ) ?? <NoValue />}
                    </span>
                    <ModeLozenge>TIMED</ModeLozenge>
                </ValueRow>
            );
        default:
            updateInterval satisfies never;
            return null;
    }
}

type Props = Readonly<{
    settings: V4SensorSettings;
    updateIntervalOptions: OptionChild<number>[];
}>;

export function CollectionSettingsSection({ settings, updateIntervalOptions }: Props) {
    const { value: lowFrequencyCollectionEnabled } = useFeatureFlag(
        'v4SensorsLowFrequencyCollection',
    );
    const allAxesDisabled = !anyVibrationAxisEnabled(settings);
    const { lowFrequencyCollection, updateInterval } = settings;

    return (
        <BorderlessSettingsSection>
            <SectionHeading>
                <SectionTitle>
                    {V4_SETTINGS_TEXT.sections.collectionSettings}
                </SectionTitle>
                <SectionDescription>
                    {V4_SETTINGS_TEXT.descriptions.collectionSettingsLocationDescription}
                </SectionDescription>
            </SectionHeading>
            <SettingsList>
                <Setting name={V4_SETTINGS_TEXT.settingNames.axesEnabled}>
                    <AxesEnabledDisplay
                        enabledAxes={getEnabledAxes({
                            x: settings.vibrationX,
                            y: settings.vibrationY,
                            z: settings.vibrationZ,
                        })}
                    />
                </Setting>
                <Setting name={V4_SETTINGS_TEXT.settingNames.updateInterval}>
                    <UpdateIntervalValue
                        updateInterval={updateInterval}
                        updateIntervalOptions={updateIntervalOptions}
                    />
                </Setting>
                {updateInterval.mode === 'triggered' && (
                    <>
                        <Setting
                            name={V4_SETTINGS_TEXT.settingNames.collectionOffsetForward}
                            indented
                        >
                            {updateInterval.collectionOffsetMs} ms
                        </Setting>
                        <Setting
                            name={V4_SETTINGS_TEXT.settingNames.minTimeBetweenTriggers}
                            indented
                        >
                            {getOptionText(
                                updateIntervalOptions,
                                updateInterval.minTimeBetweenTriggersMs,
                            ) ?? <NoValue />}
                        </Setting>
                    </>
                )}
                <Setting name={V4_SETTINGS_TEXT.settingNames.samplingFrequency}>
                    {allAxesDisabled ? (
                        <Placeholder>
                            {V4_SETTINGS_TEXT.disabledHints.allAxesDisabledHint}
                        </Placeholder>
                    ) : (
                        `${settings.samplingFrequencyHz} Hz`
                    )}
                </Setting>
                <Setting name={V4_SETTINGS_TEXT.settingNames.numberOfSamples}>
                    {allAxesDisabled ? (
                        <Placeholder>
                            {V4_SETTINGS_TEXT.disabledHints.allAxesDisabledHint}
                        </Placeholder>
                    ) : (
                        `${settings.numberOfSamples} Samples`
                    )}
                </Setting>
                {lowFrequencyCollectionEnabled && (
                    <>
                        <Setting
                            name={V4_SETTINGS_TEXT.settingNames.lowFrequencyCollection}
                            helperText={
                                V4_SETTINGS_TEXT.lowFrequencyCollection.readOnlyHelper
                            }
                        >
                            {!lowFrequencyCollection.enabled && (
                                <SettingEnabledLozenge enabled={false} />
                            )}
                        </Setting>
                        {lowFrequencyCollection.enabled && (
                            <>
                                <Setting
                                    name={V4_SETTINGS_TEXT.settingNames.samplingFrequency}
                                    indented
                                >
                                    {lowFrequencyCollection.samplingFrequencyHz} Hz
                                </Setting>
                                <Setting
                                    name={V4_SETTINGS_TEXT.settingNames.numberOfSamples}
                                    indented
                                >
                                    {lowFrequencyCollection.numberOfSamples} Samples
                                </Setting>
                            </>
                        )}
                    </>
                )}
            </SettingsList>
        </BorderlessSettingsSection>
    );
}
